import type { SubsystemSelectDto } from '@@types/api.types';
import { AssessmentType } from '@@types/api.types';
import { RadarChartComponent } from '@components/shared/radar-chart.component';
import { RadarModal } from '@components/shared/radar-modal.component';
import { Tile } from '@components/shared/tile.component';
import { useGetTechnology } from '@queries/technology.query';
import { countries } from '@utils/constants';
import { ZoomInIcon } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { AccordionTile } from '../../shared/accordion-tile.component';
import { TechnologyAssessmentHistory } from '../../technology-assessment-history.component';

const MIN_CHART_NODES = 4;

function getCountryName(code: string): string {
  const country = countries.find((c) => c.code === code);
  return country?.name ?? code;
}

function buildComponentOriginNodes(subsystems: SubsystemSelectDto[] = []) {
  const countryMap = new Map<string, { code: string; count: number; name: string }>();

  for (const subsystem of subsystems) {
    const components = subsystem.components ?? [];

    for (const component of components) {
      const countryCode = component.origin?.trim() || subsystem.origin?.trim();
      if (!countryCode) continue;

      const countryName = getCountryName(countryCode);
      const existing = countryMap.get(countryCode);

      if (existing) {
        existing.count += 1;
      } else {
        countryMap.set(countryCode, { code: countryCode, count: 1, name: countryName });
      }
    }
  }

  const nodes = Array.from(countryMap.values())
    .map((entry) => ({
      fullName: entry.name,
      label: entry.name,
      value: entry.count,
    }))
    .filter((node) => node.value > 0)
    .sort((a, b) => a.fullName.localeCompare(b.fullName));

  if (nodes.length > 0 && nodes.length < MIN_CHART_NODES) {
    let placeholderIndex = 1;
    while (nodes.length < MIN_CHART_NODES) {
      nodes.push({
        fullName: `Placeholder ${placeholderIndex}`,
        label: `Placeholder ${placeholderIndex}`,
        value: 0,
      });
      placeholderIndex++;
    }
  }

  return nodes;
}

type MrlOverviewContentProps = {
  onNavigateToAssessment?: (assessmentId: string | null) => void;
  technologyId: string;
};

export const MrlOverviewContent: FC<MrlOverviewContentProps> = (props) => {
  const { onNavigateToAssessment, technologyId } = props;

  const { data: technology } = useGetTechnology(technologyId);

  const [isOpenOriginsModal, setIsOpenOriginsModal] = useState(false);

  const componentOriginNodes = useMemo(
    () => buildComponentOriginNodes(technology?.subsystems),
    [technology?.subsystems],
  );

  const componentOriginsMaxValue = useMemo(() => {
    if (componentOriginNodes.length === 0) return 0;
    return Math.max(...componentOriginNodes.map((node) => node.value));
  }, [componentOriginNodes]);

  const mrlDescription =
    'Manufacture Readiness Level (MRL) is a measurement system used to assess the maturity of manufacturing technology, manufacturing processes, and the industrial base. The scale ranges from 1 to 10.';

  return (
    <div className="flex flex-col gap-4">
      {/* Manufacture Readiness Description */}
      <AccordionTile
        content={mrlDescription}
        title="Manufacture Readiness Level"
        value="mrl-description"
      />

      {/* Assessment History */}
      <TechnologyAssessmentHistory
        fixedType={AssessmentType.MRL}
        onDataPointClick={onNavigateToAssessment}
      />

      {/* Component Origins */}
      {componentOriginNodes.length > 0 && (
        <>
          <Tile
            header={{
              iconButton: {
                icon: <ZoomInIcon />,
                onClick: () => setIsOpenOriginsModal(true),
              },
              title: 'Component Origins',
            }}
            hoverReveal
          >
            <div className="w-full flex items-center justify-center">
              <RadarChartComponent
                label="Components"
                maxValue={componentOriginsMaxValue}
                nodes={componentOriginNodes}
              />
            </div>
          </Tile>
          {isOpenOriginsModal && (
            <RadarModal
              isOpen={isOpenOriginsModal}
              onClose={() => setIsOpenOriginsModal(false)}
              title="Component Origins"
            >
              {({ labelFontSize, size }) => (
                <RadarChartComponent
                  label="Components"
                  labelFontSize={labelFontSize}
                  layout="full-screen"
                  maxValue={componentOriginsMaxValue}
                  nodes={componentOriginNodes}
                  size={size}
                />
              )}
            </RadarModal>
          )}
        </>
      )}
    </div>
  );
};
